import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Search, FileText, Users, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MacrobaseReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const res = await axios.get(`${API}/reports/macrobase-history?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permiso para ver este reporte');
      } else {
        toast.error('Error al cargar reporte');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleSearch = () => {
    fetchReport();
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTimeout(fetchReport, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white border-b border-slate-200 p-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
          <h1 className="text-xl font-bold text-slate-900">Reporte ERP - Facturas Ingresadas</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Date Filters */}
        <Card className="p-4 border-l-4 border-teal-500" data-testid="macrobase-report-filters">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
            <Calendar className="mr-2 h-5 w-5 text-teal-600" />
            Filtrar por Fecha de Ingreso a ERP
          </h3>
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label className="text-sm">Fecha Inicio</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
                data-testid="report-start-date"
              />
            </div>
            <div className="flex-1">
              <Label className="text-sm">Fecha Fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
                data-testid="report-end-date"
              />
            </div>
            <Button onClick={handleSearch} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="report-search-btn">
              <Search className="mr-2 h-4 w-4" /> Buscar
            </Button>
            <Button variant="outline" onClick={clearFilters} data-testid="report-clear-btn">
              Limpiar
            </Button>
          </div>
        </Card>

        {loading && (
          <Card className="p-8 text-center">
            <p className="text-slate-600">Cargando reporte...</p>
          </Card>
        )}

        {data && !loading && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-teal-50 border-teal-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Total Facturas Ingresadas</p>
                    <p className="text-2xl font-bold text-teal-700" data-testid="report-total-invoices">{data.total_facturas}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-teal-400" />
                </div>
              </Card>
              <Card className="p-4 bg-emerald-50 border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Monto Total Ingresado</p>
                    <p className="text-2xl font-bold text-[#1e3a8a]" data-testid="report-total-amount">Q{data.total_general.toLocaleString('es-GT', {minimumFractionDigits: 2})}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-emerald-400" />
                </div>
              </Card>
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Usuarios que Ingresaron</p>
                    <p className="text-2xl font-bold text-blue-700">{data.summary.length}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-400" />
                </div>
              </Card>
              <Card className="p-4 bg-purple-50 border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Promedio por Factura</p>
                    <p className="text-2xl font-bold text-purple-700">Q{data.total_facturas > 0 ? (data.total_general / data.total_facturas).toLocaleString('es-GT', {minimumFractionDigits: 2}) : '0.00'}</p>
                  </div>
                  <FileText className="h-8 w-8 text-purple-400" />
                </div>
              </Card>
            </div>

            {/* Summary by User */}
            <Card className="p-6 border-l-4 border-blue-500" data-testid="macrobase-user-summary">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
                <Users className="mr-2 h-5 w-5 text-blue-600" />
                Resumen por Usuario
              </h3>
              {data.summary.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-blue-50">
                        <th className="text-left p-3 font-semibold">Usuario</th>
                        <th className="text-center p-3 font-semibold">Facturas Ingresadas</th>
                        <th className="text-right p-3 font-semibold">Monto Total</th>
                        <th className="text-right p-3 font-semibold">% del Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.summary.map((row, idx) => (
                        <tr key={idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`} data-testid={`user-row-${idx}`}>
                          <td className="p-3 font-medium">{row.nombre}</td>
                          <td className="p-3 text-center">
                            <Badge className="bg-blue-100 text-blue-700">{row.cantidad}</Badge>
                          </td>
                          <td className="p-3 text-right font-semibold">Q{row.monto_total.toLocaleString('es-GT', {minimumFractionDigits: 2})}</td>
                          <td className="p-3 text-right text-slate-600">
                            {data.total_general > 0 ? ((row.monto_total / data.total_general) * 100).toFixed(1) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-teal-50 font-bold">
                        <td className="p-3">TOTAL</td>
                        <td className="p-3 text-center">{data.total_facturas}</td>
                        <td className="p-3 text-right">Q{data.total_general.toLocaleString('es-GT', {minimumFractionDigits: 2})}</td>
                        <td className="p-3 text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-6">No hay facturas ingresadas a ERP en el periodo seleccionado</p>
              )}
            </Card>

            {/* Detail toggle */}
            {data.details.length > 0 && (
              <Card className="p-6 border-l-4 border-slate-400">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center">
                    <FileText className="mr-2 h-5 w-5 text-slate-600" />
                    Detalle de Facturas ({data.details.length})
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDetails(!showDetails)}
                    data-testid="toggle-details-btn"
                  >
                    {showDetails ? 'Ocultar Detalle' : 'Ver Detalle'}
                  </Button>
                </div>
                {showDetails && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-3 font-semibold">Proveedor</th>
                          <th className="text-left p-3 font-semibold">Sucursal</th>
                          <th className="text-center p-3 font-semibold">Tipo</th>
                          <th className="text-right p-3 font-semibold">Monto</th>
                          <th className="text-left p-3 font-semibold">Ingresado por</th>
                          <th className="text-center p-3 font-semibold">Fecha Ingreso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.details.map((d, idx) => (
                          <tr
                            key={d.id || idx}
                            className={`border-b hover:bg-slate-50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                            onClick={() => navigate(`/payments/${d.id}`)}
                          >
                            <td className="p-3 font-medium">{d.proveedor_nombre}</td>
                            <td className="p-3 text-slate-600">{d.tienda_nombre || '-'}</td>
                            <td className="p-3 text-center">
                              {d.tipo_pago === 'credito' ? (
                                <Badge className="bg-amber-500 text-white">CREDITO</Badge>
                              ) : (
                                <Badge className="bg-emerald-500 text-white">CONTADO</Badge>
                              )}
                            </td>
                            <td className="p-3 text-right font-semibold">Q{(d.monto_depositar || 0).toLocaleString('es-GT', {minimumFractionDigits: 2})}</td>
                            <td className="p-3 text-slate-600">{d.ingresada_macrobase_por}</td>
                            <td className="p-3 text-center text-slate-600">
                              {d.ingresada_macrobase_fecha ? format(new Date(d.ingresada_macrobase_fecha), 'dd/MM/yy HH:mm', { locale: es }) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
